import { Center, Spinner } from '@chakra-ui/react'
import { setUser } from '@sentry/react'
import React, { useEffect, useState } from 'react'
import useFetch, { CachePolicies } from 'use-http'
import type { UserSessionResponse } from '../interfaces'

interface ContextProps {
  user: UserSessionResponse | null
  loading: boolean
  refetch(): Promise<UserSessionResponse> | Promise<void>
}

export const UserContext = React.createContext<ContextProps>({
  user: null,
  loading: false,
  refetch: () => Promise.resolve(),
})

export const UserProvider = ({
  children,
}: {
  children: React.ReactElement
}): React.ReactElement => {
  const [initialLoad, setInitialLoad] = useState<boolean>(
    Boolean(
      window.user &&
        window.user.clientNumber &&
        window.user.imagekitId &&
        window.user.userEmail,
    ),
  )

  const {
    data: loadedUser,
    loading,
    get,
  } = useFetch<UserSessionResponse>('/api/session/', {
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    cachePolicy: CachePolicies.NO_CACHE,
    onNewData: (currData, newData: UserSessionResponse) => {
      setInitialLoad(true)
      return newData
    },
    data: window?.user ? { ...window.user } : null,
  })

  const fetchUser = async (): Promise<void> => {
    const user = await get()
    if (window.user) {
      window.user = { ...window.user, ...user }
    }
    if (!(user && user.clientNumber && user.imagekitId && user.userEmail)) {
      window.location.href = `/logout?isMediaLibraryWidget=${
        window.isMediaLibraryWidget
      }${window.widgetHost ? `&widgetHost=${window.widgetHost}` : ''}
    `
    }
  }

  useEffect(() => {
    if (
      !(
        loadedUser &&
        loadedUser.clientNumber &&
        loadedUser.imagekitId &&
        loadedUser.userEmail
      )
    ) {
      void fetchUser()
    }
  }, [])

  useEffect(() => {
    if (loadedUser) {
      setUser({
        id: loadedUser.clientNumber,
        imagekitId: loadedUser.imagekitId,
        clientNumber: loadedUser.clientNumber,
        email: loadedUser.userEmail,
      })
    }
  }, [loadedUser])

  return (
    <UserContext.Provider
      value={{ loading, user: loadedUser ?? null, refetch: fetchUser }}
    >
      {!initialLoad ? (
        <Center width="full" height="100vh">
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="appPaleGrey"
            color="appBlue"
            size="xl"
          />
        </Center>
      ) : (
        children
      )}
    </UserContext.Provider>
  )
}
